import { fromGqlId, toGqlId } from "@src/api/formatters/shopify";
import {
  FetchProductsVariantsDocument,
  FetchProductsVariantsQuery,
  FetchProductsVariantsQueryVariables,
} from "@src/generated";
import { shopifyGqlClient } from "@src/providers/gqlProvider";
import { executeChunked } from "@src/store/helpers";
import { ShopifyProduct } from "@src/store/slices/products/types";

const VARIANTS_PAGE_SIZE = 250;
const MAX_VARIANTS = 2048; // Max variants per product in Shopify

type ProductNode = Extract<
  FetchProductsVariantsQuery["nodes"][number],
  { __typename?: "Product" }
>;

export async function loadProducts(productIds: string[]) {
  return executeChunked({
    data: productIds,
    execute: loadChunk,
  });
}

async function loadChunk(ids: string[]) {
  const response = await shopifyGqlClient.query<
    FetchProductsVariantsQuery,
    FetchProductsVariantsQueryVariables
  >({
    query: FetchProductsVariantsDocument,
    variables: {
      ids: ids.map((id) => toGqlId(id, "Product")),
      first: VARIANTS_PAGE_SIZE,
    },
    fetchPolicy: "cache-first",
  });

  const productNodes = response.data.nodes.filter(
    (node): node is ProductNode => node?.__typename === "Product"
  );

  const variantsArrays = await Promise.all(productNodes.map(fetchAllVariants));

  const products: ShopifyProduct[] = productNodes.map((node, i) => ({
    id: fromGqlId(node.id),
    title: node.title,
    status: node.status,
    totalVariants: node.totalVariants,
    image: (node.featuredImage?.url ?? undefined) as string | undefined,
    handle: node.handle,
    priceRange: {
      minVariantPrice: parseNumber(node.priceRange.minVariantPrice),
      maxVariantPrice: parseNumber(node.priceRange.maxVariantPrice),
    },
    variants: variantsArrays[i],
  }));

  return products;
}

async function fetchAllVariants(
  node: ProductNode
): Promise<ShopifyProduct["variants"]> {
  const edges = [...node.variants.edges];
  let after: string | null = node.variants.pageInfo.endCursor ?? null;
  let hasNextPage = node.variants.pageInfo.hasNextPage;

  // Paginate through remaining variant pages (API returns 250 per page, cap at 2,048)
  while (hasNextPage && after && edges.length < MAX_VARIANTS) {
    const response = await shopifyGqlClient.query<
      FetchProductsVariantsQuery,
      FetchProductsVariantsQueryVariables
    >({
      query: FetchProductsVariantsDocument,
      variables: {
        ids: [node.id],
        first: VARIANTS_PAGE_SIZE,
        after,
      },
      fetchPolicy: "network-only",
    });
    const product = response.data.nodes[0];
    if (product?.__typename !== "Product") {
      break;
    }
    const pageInfo = product.variants.pageInfo;
    // Guard against infinite loop if cursor doesn't advance (in the unlikely event caching causes it)
    if (pageInfo.endCursor === after) {
      break;
    }

    edges.push(...product.variants.edges);
    after = pageInfo.endCursor ?? null;
    hasNextPage = pageInfo.hasNextPage;
  }

  return edges.map((e) => ({
    id: fromGqlId(e.node.id),
    title: e.node.title,
    price: parseNumber(e.node.price),
    compareAtPrice: parseNumberOrUndefined(e.node.compareAtPrice),
  }));
}

function parseNumberOrUndefined(value: unknown) {
  if (typeof value === "string") {
    return value;
  } else if (typeof value === "number") {
    return isNaN(value) ? undefined : value.toString();
  } else {
    return undefined;
  }
}

function parseNumber(value: unknown) {
  if (typeof value === "string") {
    return value;
  } else if (typeof value === "number") {
    return isNaN(value) ? "0" : value.toString();
  } else {
    return "0";
  }
}
